// Catches a crash while drawing a screen: a calm page with a way out instead of a white screen,
// and a js_error event (kind: render) so we see it in analytics.
import { Component, type ErrorInfo, type ReactNode } from 'react';
import i18n from '@/lib/i18n';
import { flush, reportError, track } from '@/lib/analytics';
import { supportMailto, trackEmailClick } from '@/features/help/support';

type State = { error: Error | null };

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const where = (info.componentStack ?? '').trim().split('\n')[0]?.trim().slice(0, 120);
    reportError('render', error.message || String(error), where);
    track('error_shown', { code: 'crash', path: location.pathname });
    void flush();
  }

  render() {
    if (!this.state.error) return this.props.children;
    const t = i18n.getFixedT(null, 'help');
    return (
      <div className="screen crash-screen" role="alert">
        <div className="help-page" style={{ justifyContent: 'center', minHeight: '100dvh' }}>
          <h1 className="lg-title">{t('crash.title')}</h1>
          <p className="hint">{t('crash.lead')}</p>
          <button className="btn btn-primary btn-block" type="button" onClick={() => location.reload()}>{t('crash.reload')}</button>
          <a className="btn btn-quiet btn-block" href="/">{t('crash.home')}</a>
          <a
            className="btn btn-text btn-block"
            href={supportMailto({ extra: `Error: ${this.state.error.message.slice(0, 200)}` })}
            onClick={() => trackEmailClick('crash')}
          >
            {t('crash.email')}
          </a>
        </div>
      </div>
    );
  }
}
